import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const PAGE_SIZE = 3;

export const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data.testimonials || []));
  }, []);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const visible = items.slice(start, start + PAGE_SIZE);
  const verifiedCount = items.filter((t) => t.source === "customer").length;
  const avgRating =
    items.length > 0
      ? (items.reduce((s, t) => s + (t.rating || 5), 0) / items.length).toFixed(1)
      : null;

  // Auto-rotate every 9s if there are multiple pages
  useEffect(() => {
    if (totalPages < 2) return;
    const t = setInterval(() => setPage((p) => (p + 1) % totalPages), 9000);
    return () => clearInterval(t);
  }, [totalPages]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-white py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-14">
          <span className="font-accent text-[#C5A059] text-xs">TRUSTED BY FOUNDERS</span>
          <h2 className="font-display text-4xl sm:text-5xl text-[#0A192F] mt-4 mb-3">
            Real founders. <span className="italic">Real launches.</span>
          </h2>
          {avgRating && items.length >= 3 && (
            <div className="inline-flex items-center gap-2 text-sm text-slate-500 mt-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-4 h-4 ${
                      n <= Math.round(avgRating)
                        ? "fill-[#C5A059] text-[#C5A059]"
                        : "text-slate-300"
                    }`}
                  />
                ))}
              </div>
              <span data-testid="testimonials-avg-rating">
                <b className="text-[#0A192F]">{avgRating}</b>
                {verifiedCount > 0 ? (
                  <> based on <b className="text-[#0A192F]">{verifiedCount}</b> verified review{verifiedCount === 1 ? "" : "s"}</>
                ) : (
                  <> across <b className="text-[#0A192F]">{items.length}</b> founder stories</>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8" key={page}>
          {visible.map((t, idx) => (
            <article
              key={t.id}
              data-testid={`testimonial-${idx}`}
              className="bg-[#F8FAFC] border border-slate-200 p-8 relative fade-up flex flex-col"
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              <Quote className="absolute top-6 right-6 w-8 h-8 text-[#C5A059]/30" />
              <div className="flex gap-1 mb-5">
                {[...Array(t.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-6 text-[15px] flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-5 border-t border-slate-200">
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover border border-[#C5A059]/40"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#0A192F] text-[#C5A059] flex items-center justify-center font-display text-base">
                    {(t.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-display text-[#0A192F]">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10" data-testid="testimonials-carousel-controls">
            <button
              data-testid="testimonials-prev"
              onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
              className="w-9 h-9 border border-slate-300 hover:border-[#C5A059] hover:text-[#C5A059] text-slate-500 flex items-center justify-center transition"
              aria-label="Previous reviews"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`h-1.5 transition-all ${
                    i === page ? "w-6 bg-[#C5A059]" : "w-1.5 bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                  data-testid={`testimonials-dot-${i}`}
                />
              ))}
            </div>
            <button
              data-testid="testimonials-next"
              onClick={() => setPage((p) => (p + 1) % totalPages)}
              className="w-9 h-9 border border-slate-300 hover:border-[#C5A059] hover:text-[#C5A059] text-slate-500 flex items-center justify-center transition"
              aria-label="Next reviews"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
